import { useRef, useMemo, useCallback, useState, useEffect } from 'react';

const NumericDistributionViewer = ({ table, columnName, searchIndices, minVal, maxVal, range, onChange, onMouseUp, isLogScale }) => {
  const containerRef = useRef(null);
  const [dragging, setDragging] = useState(null); // 'start', 'end', or 'window'
  const [hoveredHandle, setHoveredHandle] = useState(null);
  const dragDataRef = useRef(null);

  const safeRange = useMemo(() => [
    Math.max(0, Math.min(1, isNaN(range[0]) ? 0 : range[0])),
    Math.max(0, Math.min(1, isNaN(range[1]) ? 1 : range[1]))
  ], [range]);

  const isDateCol = useMemo(() => /date/i.test(columnName), [columnName]);

  // levels to match TimePlotViewer grid lines
  const levels = [1, 0.75, 0.5, 0.25, 0];

  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(entries => setWidth(entries[0].contentRect.width));
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const tickConfig = useMemo(() => {
    if (width < 250) return { size: '7px', steps: [0, 1] };        // Only Start/End
    if (width < 450) return { size: '8px', steps: [0, 0.5, 1] };   // Start/Mid/End
    return { size: '9px', steps: [0, 0.25, 0.5, 0.75, 1] };        // All 5
  }, [width]);

  const { bins, maxRawCount } = useMemo(() => {
    const numBins = 200;
    const counts = new Array(numBins).fill(0);
    const col = table.getChild(columnName);
    const source = searchIndices || Array.from({ length: table.numRows }, (_, i) => i);
    for (const i of source) {
      const v = col.get(i);
      if (v == null || isNaN(v)) continue;
      // Linear binning for X-axis
      let b = Math.floor(((v - minVal) / (maxVal - minVal || 1)) * numBins);
      b = Math.max(0, Math.min(numBins - 1, b));
      counts[b]++;
    }
    const maxRaw = Math.max(...counts);
    
    // Apply log scale only to the height (Y-axis)
    const processedBins = counts.map(c => {
      if (maxRaw === 0) return 0;
      if (isLogScale) {
        // using log10(c + 1) so that a count of 1 results in a visible height, and 0 remains 0
        return Math.log10(c + 1) / Math.log10(maxRaw + 1);
      }
      return c / maxRaw;
    });

    return { bins: processedBins, maxRawCount: maxRaw };
  }, [table, columnName, searchIndices, minVal, maxVal, isLogScale]);

  const fmt = useCallback((v) => {
    if (isDateCol) {
      const date = new Date(v);
      if (isNaN(date.getTime())) return "";
      const [y, m, d] = date.toISOString().split('T')[0].split('-');
      return `${d}/${m}/${y}`;
    }
    if (v === 0) return "0";
    if (Math.abs(v) < 0.01 || Math.abs(v) > 10000) return v.toExponential(2);
    return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }, [isDateCol]);

  // Keep a ref of the latest onMouseUp function
  const onMouseUpRef = useRef(onMouseUp);
  useEffect(() => {
    onMouseUpRef.current = onMouseUp;
  }, [onMouseUp]);

  const handleMouseMove = useCallback((e) => {
    if (!dragging || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    
    if (dragging === 'window') {
      if (!dragDataRef.current) return;
      const { startX, startRange } = dragDataRef.current;
      let delta = (e.clientX - startX) / rect.width;
      
      const minVal = Math.min(...startRange);
      const maxVal = Math.max(...startRange);
      
      // Constrain sliding to boundaries [0, 1]
      if (minVal + delta < 0) delta = -minVal;
      if (maxVal + delta > 1) delta = 1 - maxVal;
      
      onChange([startRange[0] + delta, startRange[1] + delta]);
    } else {
      const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const newRange = [...safeRange];
      if (dragging === 'start') newRange[0] = x;
      else if (dragging === 'end') newRange[1] = x;
      onChange(newRange);
    }
  }, [dragging, safeRange, onChange]);

  useEffect(() => {
    const up = () => {
      if (dragging) {
        onMouseUpRef.current?.(); 
        setDragging(null);
      }
    };
    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', up);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', up);
    };
  }, [dragging, handleMouseMove]); 

  return (
    <div className="flex flex-col h-full w-full gap-0 p-2">
      <div className="flex flex-row flex-grow min-h-0">
        
        {/* Y-Axis Gutter */}
        <div className="flex flex-col justify-between items-end text-[9px] font-mono text-gray-400 pr-2 py-0 select-none border-r border-gray-200">
          {levels.map(p => (
            <span key={p}>
            {(isLogScale 
              ? (Math.pow(10, p * Math.log10(maxRawCount + 1)) - 1) 
              : (p * maxRawCount)).toLocaleString(undefined, { maximumSignificantDigits: 3 })}
          </span>
          ))}
        </div>

        <div ref={containerRef} className="relative flex-grow bg-slate-50/50 select-none">
          
          {/* Horizontal Grid Lines*/}
          <div className="absolute inset-0 pointer-events-none opacity-30">
            {levels.map(p => (
              <div 
                key={p} 
                className={`absolute w-full ${p !== 0 ? "border-t border-gray-400 border-dashed" : "border-t border-blue-400"}`} 
                style={{ top: `${(1 - p) * 100}%` }}
              />
            ))}
          </div>

          <svg className="w-full h-full relative z-10" preserveAspectRatio="none">
            <rect
              x={`${Math.min(safeRange[0], safeRange[1]) * 100}%`}
              width={`${Math.abs(safeRange[1] - safeRange[0]) * 100}%`}
              height="100%"
              fill="rgba(59, 153, 246, 0.1)"
            />
            {bins.map((h, i) => {
              const barPos = i / bins.length;
              const activeMin = Math.min(safeRange[0], safeRange[1]);
              const activeMax = Math.max(safeRange[0], safeRange[1]);
              const isActive = barPos >= activeMin && barPos <= activeMax;
              return (
                <rect
                  key={i}
                  x={`${(i / bins.length) * 100}%`}
                  y={`${(1 - h) * 100}%`}
                  width={`${100 / bins.length}%`}
                  height={`${h * 100}%`}
                  fill={isActive ? '#3b82f6' : '#1b74e857'}
                />
              );
            })}
          </svg>

          {/* Draggable Window Overlay */}
          <div 
            className="absolute top-0 bottom-0 z-20"
            style={{
              left: `${Math.min(safeRange[0], safeRange[1]) * 100}%`,
              width: `${Math.abs(safeRange[1] - safeRange[0]) * 100}%`,
              cursor: dragging === 'window' ? 'grabbing' : 'grab'
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              dragDataRef.current = {
                startX: e.clientX,
                startRange: [...safeRange]
              };
              setDragging('window');
            }}
          />
          
          {[
            { id: 'start', pos: safeRange[0] },
            { id: 'end', pos: safeRange[1] }
          ].map(handle => (
            <div 
              key={handle.id}
              className="absolute top-0 bottom-0 w-4 -ml-2 z-30 group"
              style={{ left: `${handle.pos * 100}%` }}
              onMouseEnter={() => setHoveredHandle(handle.id)}
              onMouseLeave={() => setHoveredHandle(null)}
              onMouseDown={(e) => { e.stopPropagation(); setDragging(handle.id); }}
            >
              {/* Vertical Blue Line */}
              <div className={`absolute inset-y-0 left-1/2 -translate-x-1/2 w-1 bg-blue-600 transition-opacity cursor-ew-resize ${dragging === handle.id || hoveredHandle === handle.id ? 'opacity-80' : 'opacity-30'}`} />
              {/* Knob */}
              <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-8 bg-white rounded-full border-2 shadow-md cursor-ew-resize ${dragging === handle.id || hoveredHandle === handle.id ? 'border-blue-600' : 'border-blue-500'}`} />
            </div>
          ))}
        </div>
      </div>

      {/* X-Axis Footer */}
      <div className="h-4 flex flex-row border-t border-gray-200 mt-1">
        <div className="w-8 border-r border-transparent" /> 
        <div className="flex-grow relative text-mono text-gray-400 mt-0.5" style={{ fontSize: tickConfig.size }}>
          {tickConfig.steps.map(s => (
            <span 
              key={s} 
              className="absolute whitespace-nowrap" 
              style={{ 
                left: `${s * 100}%`, 
                transform: s === 0 ? 'none' : s === 1 ? 'translateX(-100%)' : 'translateX(-50%)' 
              }}
            >
              {fmt(minVal + s * (maxVal - minVal))}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NumericDistributionViewer;